import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useConnectStripeWithKeys } from '../hooks/use-stripe-mutations';
import { isProviderRejected, toDirectKeys } from '../schemas/stripe-rules';
import { stripeDirectKeysSchema, type StripeDirectKeysValues } from '../schemas/stripe-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['secretKey', 'publishableKey', 'webhookSecret']);

/**
 * The documented exception to Connect: the tenant pastes keys from their own Stripe dashboard.
 * The secret key is checked against Stripe before it is stored and is never shown again.
 */
export function StripeDirectKeysForm({
  storeId,
  replacing = false,
}: {
  storeId: string;
  /** Rotating keys on a live connection: the stored webhook secret and Apple Pay domains stay. */
  replacing?: boolean;
}) {
  const { t } = useTranslation(['paymentMethods', 'errors', 'common']);
  const connect = useConnectStripeWithKeys(
    storeId,
    replacing
      ? 'paymentMethods:stripe.toasts.keysReplaced'
      : 'paymentMethods:stripe.toasts.connected',
  );
  const message = useErrorMessage();
  const form = useForm<StripeDirectKeysValues>({
    resolver: useI18nResolver(stripeDirectKeysSchema),
    defaultValues: { secretKey: '', publishableKey: '', webhookSecret: '' },
  });
  const serverError = form.formState.errors.root?.server?.message;
  const requestId = isApiError(connect.error) ? connect.error.requestId : undefined;

  const submit = form.handleSubmit(async (values) => {
    try {
      await connect.mutateAsync(toDirectKeys(values));
      if (replacing) form.reset();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', {
        type: 'server',
        message: isProviderRejected(error)
          ? t('paymentMethods:stripe.keys.rejected')
          : message(error),
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label={t('paymentMethods:stripe.keys.formLabel')}
      >
        <p className="text-muted-foreground text-sm">{t('paymentMethods:stripe.keys.intro')}</p>
        {serverError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {serverError}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="secretKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('paymentMethods:stripe.keys.secretKey')}</FormLabel>
              <FormControl>
                <PasswordInput
                  showLabel={t('common:password.showSecret')}
                  hideLabel={t('common:password.hideSecret')}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-[13px]"
                  placeholder="rk_live_…"
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('paymentMethods:stripe.keys.secretKeyHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="publishableKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('paymentMethods:stripe.keys.publishableKey')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-[13px]"
                  placeholder="pk_live_…"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="webhookSecret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('paymentMethods:stripe.keys.webhookSecret')}</FormLabel>
              <FormControl>
                <PasswordInput
                  showLabel={t('common:password.showSecret')}
                  hideLabel={t('common:password.hideSecret')}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-[13px]"
                  placeholder="whsec_…"
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('paymentMethods:stripe.keys.webhookSecretHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div>
          <Button type="submit" variant="outline" disabled={connect.isPending}>
            {connect.isPending
              ? t('paymentMethods:stripe.keys.submitting')
              : replacing
                ? t('paymentMethods:stripe.keys.replaceSubmit')
                : t('paymentMethods:stripe.keys.submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
